// APP-1327 (s-security-master-permission-error-019effd3): shared builder for the
// permission-denied (403) case across write handlers. A failure that is a 403 —
// the API's RolesGuard rejecting a non-admin write — should read as a permissions
// issue, not a system bug; every other failure must keep that call site's generic
// "Failed to ..." copy so real errors are not masked. The decision is generic
// (error in, 403-vs-generic out) and not specific to any one screen, so it lives
// here in `lib` rather than alongside the Security Master feature.

import { isForbiddenError } from "./api";

/** Toast header shown when a non-admin is denied an admin-only write. */
export const ADMIN_PERMISSION_REQUIRED_HEADER = "Admin Permission Required";

/**
 * Builds the permission-denied body naming the specific `subject` the user tried
 * to edit (e.g. "the Security Master", "Asset Allocations"), so the message reads
 * naturally on the screen the user is actually on.
 */
export function buildPermissionDeniedBody(subject: string): string {
  return `You need admin permissions to edit ${subject}. Contact an admin to make changes.`;
}

/** Shape of a danger toast pushed via `NotificationContext.pushNotification`. */
export type PermissionAwareErrorNotification = {
  id: string;
  header: string;
  body: string;
  variant: "danger";
};

/**
 * Builds the danger toast for a failed write, choosing the copy based on whether
 * the failure was a 403 permission denial.
 *
 * APP-1327 (s-security-master-permission-error-019effd3): write handlers that
 * gate on the Admin role all need the same branch — a 403 (no Admin role) gets
 * the shared permission-denied copy naming `subject`, every other failure
 * (network, 5xx, validation) keeps that call site's generic copy so real errors
 * are not masked as a permissions problem. Concentrating the decision here keeps
 * the handlers identical and makes the branch unit-testable.
 *
 * @param error The value caught from the failed request (mutation/await reject).
 * @param id The notification id for this call site (e.g. "security-master-save").
 * @param genericHeader Header to use when the error is NOT a 403.
 * @param genericBody Body to use when the error is NOT a 403.
 * @param subject The thing the user tried to edit, named in the permission-denied
 *   body (e.g. "Asset Allocations"). Required — every call site names its screen.
 * @returns A `danger`-variant notification with permission-denied copy on a 403,
 *   otherwise the passed generic header/body.
 */
export function permissionAwareErrorNotification(
  error: unknown,
  id: string,
  genericHeader: string,
  genericBody: string,
  subject: string,
): PermissionAwareErrorNotification {
  const permissionDenied = isForbiddenError(error);
  return {
    id,
    header: permissionDenied ? ADMIN_PERMISSION_REQUIRED_HEADER : genericHeader,
    body: permissionDenied ? buildPermissionDeniedBody(subject) : genericBody,
    variant: "danger",
  };
}
